/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  message,
  Select,
  Card,
  Tag,
  Button,
  InputNumber,
  Table,
  Modal,
  Alert,
} from "antd";
import { PlusOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { pricingApi, tripsApi, schedulesApi, boatApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";

const { Option } = Select;

const PricingPage = () => {
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [trips, setTrips] = useState([]);
  const [decks, setDecks] = useState([]);
  const [tripDates, setTripDates] = useState([]);
  const [pricings, setPricings] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [addModal, setAddModal] = useState({ visible: false });
  const [addForm, setAddForm] = useState({ deckId: "", price: 0 });
  const [addLoading, setAddLoading] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const userPermissions = currentUser?.permissions || [];

  // Check permissions
  const canViewPricing = hasPermission(userPermissions, "pricing.view");
  const canCreatePricing = hasPermission(userPermissions, "pricing.create");
  const canEditPricing = hasPermission(userPermissions, "pricing.edit");
  const canDeletePricing = hasPermission(userPermissions, "pricing.delete");

  useEffect(() => {
    if (canViewPricing) {
      Promise.all([tripsApi.getAll({ limit: 100 }), boatApi.getAllDecks()])
        .then(([t, d]) => {
          setTrips(t.data || []);
          setDecks(Array.isArray(d) ? d : []);
        })
        .catch(() => message.error("Failed to load data"));
    }
  }, [canViewPricing]);

  const handleTripChange = async (tripId) => {
    setSelectedTrip(tripId);
    setSelectedDate("");
    setPricings([]);
    setTripDates([]);
    if (!tripId) return;
    try {
      const r = await schedulesApi.getByTrip(tripId, { limit: 200 });
      setTripDates((r.data || []).filter((d) => d.status === "active"));
    } catch {
      message.error("Failed to load dates");
    }
  };

  const handleDateChange = async (tripDateId) => {
    setSelectedDate(tripDateId);
    if (!tripDateId) {
      setPricings([]);
      return;
    }

    if (!canViewPricing) {
      message.error("You don't have permission to view pricing");
      return;
    }

    try {
      setLoading(true);
      const r = await pricingApi.getDatePricings(tripDateId);
      setPricings(Array.isArray(r) ? r : []);
    } catch (error) {
      if (error.response?.status === 403) {
        message.error("You don't have permission to view pricing");
      } else {
        message.error("Failed to load price overrides");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!canCreatePricing) {
      message.error("You don't have permission to create price overrides");
      setAddModal({ visible: false });
      return;
    }

    if (!addForm.deckId || !addForm.price) {
      return message.error("Select a deck and enter a price");
    }

    try {
      setAddLoading(true);
      await pricingApi.setDatePricing(selectedDate, {
        deckId: addForm.deckId,
        price: addForm.price,
      });
      message.success("Price override saved");
      setAddModal({ visible: false });
      setAddForm({ deckId: "", price: 0 });
      handleDateChange(selectedDate);
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to create price overrides");
      } else {
        message.error(e.response?.data?.message || "Failed to save");
      }
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async (deckId) => {
    if (!canDeletePricing) {
      message.error("You don't have permission to delete price overrides");
      return;
    }

    try {
      await pricingApi.deleteDatePricing(selectedDate, deckId);
      message.success("Override removed — trip default price will apply");
      handleDateChange(selectedDate);
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to delete price overrides");
      } else {
        message.error(e.response?.data?.message || "Failed to remove");
      }
    }
  };

  const columns = [
    {
      title: "Deck",
      dataIndex: ["deck", "name"],
      key: "deck",
      render: (_, r) => <strong>{r.deck?.name || r.deckId}</strong>,
    },
    {
      title: "Override Price",
      dataIndex: "price",
      key: "price",
      render: (v) => (
        <Tag color="orange" style={{ fontSize: 14, padding: "2px 12px" }}>
          EGP {Number(v).toLocaleString()}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => {
        if (!canDeletePricing) return null;

        return (
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(r.deckId)}
          >
            Remove Override
          </Button>
        );
      },
    },
  ].filter((col) => {
    // If user can't delete, remove the Actions column
    if (col.key === "actions" && !canDeletePricing) return false;
    return true;
  });

  // Permission check for viewing the page
  if (!canViewPricing) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              message="Access Denied"
              description="You don't have permission to view pricing. Please contact your administrator if you believe this is an error."
              type="error"
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>Date Pricing Overrides</h5>
                  {!canCreatePricing && !canDeletePricing && (
                    <Tag icon={<EyeOutlined />} color="blue">
                      View Only Mode
                    </Tag>
                  )}
                </div>
              </div>

              <Alert
                message="Price overrides let you set special prices for specific dates (e.g. holidays). They override trip-level prices only for the selected date."
                type="info"
                showIcon
                className="mb-4"
              />

              {!canCreatePricing && !canDeletePricing && (
                <Alert
                  message="View Only Mode"
                  description="You have view-only access to pricing. To add or remove overrides, please contact an administrator."
                  type="warning"
                  showIcon
                  className="mb-4"
                />
              )}

              <div className="row mb-4">
                <div className="col-md-4">
                  <div className="input-block mb-3">
                    <label>Select Trip</label>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Choose a trip"
                      value={selectedTrip || undefined}
                      onChange={handleTripChange}
                      showSearch
                      optionFilterProp="children"
                      allowClear
                    >
                      {trips.map((t) => (
                        <Option key={t.id} value={t.id}>
                          {t.name}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="input-block mb-3">
                    <label>Select Date</label>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Choose a date"
                      value={selectedDate || undefined}
                      onChange={handleDateChange}
                      disabled={!selectedTrip}
                      allowClear
                      showSearch
                    >
                      {tripDates.map((d) => (
                        <Option key={d.id} value={d.id}>
                          {d.date}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </div>
                {selectedDate && canCreatePricing && (
                  <div
                    className="col-md-4"
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      paddingBottom: 12,
                    }}
                  >
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "#7B4D12",
                        borderColor: "#7B4D12",
                      }}
                      icon={<PlusOutlined />}
                      onClick={() => setAddModal({ visible: true })}
                    >
                      Add Override
                    </Button>
                  </div>
                )}
              </div>

              {selectedDate && (
                <Table
                  columns={columns}
                  dataSource={pricings}
                  rowKey="id"
                  loading={loading}
                  pagination={false}
                  locale={{
                    emptyText:
                      pricings.length === 0 && !loading
                        ? "No price overrides for this date — trip defaults apply"
                        : "Loading...",
                  }}
                />
              )}

              {!selectedDate && (
                <div className="text-center py-5 text-muted">
                  Select a trip and date to view or manage price overrides.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Override Modal - Only show if user has create permission */}
      {canCreatePricing && (
        <Modal
          title="Add Price Override"
          open={addModal.visible}
          onOk={handleAdd}
          onCancel={() => {
            setAddModal({ visible: false });
            setAddForm({ deckId: "", price: 0 });
          }}
          confirmLoading={addLoading}
          okText="Save Override"
        >
          <div className="input-block mb-3">
            <label>
              Deck <span className="text-danger">*</span>
            </label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select deck"
              value={addForm.deckId || undefined}
              onChange={(v) => setAddForm((f) => ({ ...f, deckId: v }))}
              allowClear
            >
              {decks.map((d) => (
                <Option key={d.id} value={d.id}>
                  {d.name}
                </Option>
              ))}
            </Select>
          </div>
          <div className="input-block mb-3">
            <label>
              Override Price (EGP) <span className="text-danger">*</span>
            </label>
            <InputNumber
              style={{ width: "100%" }}
              min={1}
              value={addForm.price}
              onChange={(v) => setAddForm((f) => ({ ...f, price: v }))}
            />
            <small className="text-muted">
              This price will apply only for the selected date on this deck.
            </small>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PricingPage;
